import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import PurchaseOrderTable from "./components/PurchaseOrderTable";
import { useGetPurchaseOrders } from "./useGetPurchaseOrders";
import {
  useSubmitPurchaseOrder,
  useApprovePurchaseOrder,
  useConvertPurchaseOrder,
  useCancelPurchaseOrder,
  useDeletePurchaseOrder,
} from "./usePurchaseOrderMutations";
import toast from "react-hot-toast";

const STATUS_FILTERS = [
  { key: "All", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pendingApproval", label: "Pending Approval" },
  { key: "approved", label: "Approved" },
  { key: "convertedToBill", label: "Converted" },
  { key: "cancelled", label: "Cancelled" },
];

const PurchaseOrdersContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const { data: poData = [], isLoading, isError } = useGetPurchaseOrders();

  const [poList, setPoList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPoList(poData);
  }, [poData]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const filtered = poList.filter((po) => {
    const matchesSearch =
      !searchTerm.trim() ||
      po.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || po.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const [busyId, setBusyId] = useState(null);
  const submitPO = useSubmitPurchaseOrder();
  const approvePO = useApprovePurchaseOrder();
  const convertPO = useConvertPurchaseOrder();
  const cancelPO = useCancelPurchaseOrder();
  const deletePO = useDeletePurchaseOrder();

  const runAction = async (id, mutation, args, successMsg) => {
    setBusyId(id);
    try {
      await mutation.mutateAsync(args);
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleSubmit = (po) =>
    runAction(po.id, submitPO, po.id, "Submitted for approval");
  const handleApprove = (po) =>
    runAction(po.id, approvePO, po.id, "Purchase order approved");
  const handleCancel = (po) =>
    runAction(po.id, cancelPO, po.id, "Purchase order cancelled");
  const handleConvert = (po) =>
    runAction(
      po.id,
      convertPO,
      { id: po.id },
      "Bill created from purchase order",
    );
  const handleDelete = (id) =>
    runAction(id, deletePO, id, "Purchase order deleted");

  // Stats
  const totalAmount = poList.reduce(
    (s, po) => s + (parseFloat(po.netAmount) || 0),
    0,
  );
  const statusCounts = poList.reduce((acc, po) => {
    acc[po.status] = (acc[po.status] || 0) + 1;
    return acc;
  }, {});

  const getStatusLabel = (status) => {
    const key = status.toLowerCase();
    return key === "all"
      ? t("common.all")
      : t(`status.${key}`, { defaultValue: status });
  };

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  if (isError)
    return (
      <div
        className={`transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"} p-8`}
      >
        <div className="flex items-center justify-center min-h-[400px] flex-col gap-4">
          <Icon name="AlertTriangle" size={48} className="text-red-500" />
          <p className="text-muted-foreground">
            {t("purchaseOrders.failedLoad")}
          </p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t("actions.retry")}
          </Button>
        </div>
      </div>
    );

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("purchaseOrders.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("purchaseOrders.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("actions.export")}
              </Button>
              <Button
                variant="default"
                size="md"
                iconName="Plus"
                onClick={() => navigate("/purchase-order-wizard")}
              >
                {t("purchaseOrders.createOrder")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {[
            {
              label: t("purchaseOrders.stats.totalOrders"),
              value: poList.length,
              icon: "ShoppingCart",
              color:
                "text-blue-500 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
            },
            {
              label: t("purchaseOrders.stats.totalValue"),
              value: `SR ${totalAmount.toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
              icon: "DollarSign",
              color:
                "text-green-500 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
            },
            {
              label: t("purchaseOrders.stats.pending", {
                defaultValue: "Pending Approval",
              }),
              value: statusCounts["pendingApproval"] || 0,
              icon: "Clock",
              color:
                "text-amber-500 bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400",
            },
            {
              label: t("purchaseOrders.stats.converted", {
                defaultValue: "Converted to Bill",
              }),
              value: statusCounts["convertedToBill"] || 0,
              icon: "PackageCheck",
              color:
                "text-teal-500 bg-teal-100 dark:bg-teal-900/20 dark:text-teal-400",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-card rounded-xl p-4 shadow-sm border border-border flex items-center gap-3"
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`}
              >
                <Icon name={stat.icon} size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold text-foreground truncate">
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800 flex gap-3"
        >
          <div className="flex-shrink-0 mt-0.5">
            <Icon
              name="AlertCircle"
              size={18}
              className="text-amber-600 dark:text-amber-400"
            />
          </div>
          <div className="text-sm text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Important, please read: </span>A
            Purchase Order is not an actual purchase of goods or services. It is
            just an order placed with the vendor to make a purchase. If you have
            already made a purchase, to record a purchase bill please go to the{" "}
            <button
              onClick={() => navigate("/expenses")}
              className="underline font-medium hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
            >
              Expenses section
            </button>{" "}
            and click on <span className="font-medium">"Add New Expense"</span>,
            then choose the appropriate option to record a purchase bill.
          </div>
        </motion.div>

        {/* Status Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-6"
        >
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((f) => {
              const count =
                f.key === "All" ? poList.length : statusCounts[f.key] || 0;
              return (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    statusFilter === f.key
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-card text-muted-foreground hover:bg-muted border border-border"
                  }`}
                >
                  {f.label}
                  <span
                    className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                      statusFilter === f.key
                        ? "bg-white/20"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.12 }}
          className="bg-card rounded-xl p-5 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("purchaseOrders.searchPlaceholder")}
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={15} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <PurchaseOrderTable
            purchaseOrders={paginated}
            busyId={busyId}
            onEdit={(po) =>
              navigate("/purchase-order-wizard", { state: { editingPO: po } })
            }
            onSubmit={handleSubmit}
            onApprove={handleApprove}
            onConvert={handleConvert}
            onCancel={handleCancel}
            onDelete={handleDelete}
          />
        </motion.div>

        {/* Empty State */}
        {filtered.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-4"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="ShoppingCart" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("purchaseOrders.emptyTitle")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "All"
                ? t("common.adjustFiltersOrSearch")
                : t("purchaseOrders.createFirst")}
            </p>
            {!searchTerm && statusFilter === "All" && (
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => navigate("/purchase-order-wizard")}
              >
                {t("purchaseOrders.createOrder")}
              </Button>
            )}
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t("common.showingRange", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, filtered.length),
                total: filtered.length,
                entity: t("entities.purchaseOrders"),
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              <span className="text-sm text-foreground px-2">
                {t("common.pageOf", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const PurchaseOrders = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PurchaseOrdersContent />
    </div>
  </SidebarProvider>
);

const LoadingSkeleton = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
  >
    <main className="p-4 md:p-6 animate-pulse">
      <div className="h-8 bg-muted rounded w-64 mb-2" />
      <div className="h-4 bg-muted rounded w-96 mb-8" />
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl" />
        ))}
      </div>
      <div className="h-16 bg-muted rounded-xl mb-6" />
      <div className="h-12 bg-muted rounded-xl mb-6" />
      <div className="bg-card rounded-xl border border-border p-6 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 bg-muted rounded" />
        ))}
      </div>
    </main>
  </div>
);

export default PurchaseOrders;
